/**
 * "Relocate a resident" guided flow.
 *
 * Consumed by src/components/help/HelpGuide.jsx. All user-facing copy lives
 * here; components render it verbatim. See docs/INTERACTIVE_HELP_PLAN.md §4 for
 * the shape and §6.3 for why each detector is what it is.
 */

export default {
    id: 'relocate-resident',
    title: 'Relocate a resident',
    description: 'Move a resident to a different room.',

    /* Caregivers are weight 10 and can't relocate, so every v1 flow sits at 11.
       The FAB is gated the same way (§7); this is per-flow defence in depth so a
       future lower-privilege flow can't accidentally inherit a blanket gate. */
    minRoleWeight: 11,

    /* EHR-managed campuses relocate residents by approving the EHR's task, so
       this is replaced there by relocateResidentEhr. Exactly complementary gates,
       no weight-80 override — see moveInResident for why. Hidden, never shown
       disabled (Decision 2). */
    available: (ctx) => !ctx.ehrActive,

    /* Shown in the picker before the user commits, because starting the flow
       navigates away and could discard unsaved work on the current page. */
    startsAt: 'This will take you to the Residents page.',

    steps: [
        {
            id: 'open-dialog',
            route: { path: '/community/residents', navigate: true },
            anchor: 'residents.row-relocate',
            title: 'Pick the resident',
            body: 'Click the relocate icon next to the area of the resident you want to move.',
            placement: 'left',
            /* The dialog rendering at all means relocateResidentRow is set. */
            done: { type: 'dom', selector: '[data-help-id="relocate-dialog.submit"]' },
            /* No timeout: this waits on a human, and the escape hatches are
               always present. */
        },
        {
            id: 'choose-and-save',
            route: { path: '/community/residents', navigate: false },
            anchor: 'relocate-dialog.area-select',
            title: 'Choose the new area, then save',
            body: 'Check the resident’s name at the top of the dialog, then search or pick the destination area and click Relocate. Rooms already at capacity are listed but can’t be selected.',
            placement: 'right',
            /* If the dialog closes before the save lands, the user backed out —
               regress rather than sit on a step whose anchor is gone. */
            context: {
                selector: '[data-help-id="relocate-dialog.submit"]',
                contextStep: 'open-dialog',
            },
            /* Same transType from both entry points (list and profile), so this
               detector is entry-agnostic. */
            done: { type: 'transaction', transTypes: ['resident_move_profile'] },
            timeoutMs: 15000,
        },
    ],

    success: {
        title: 'Resident relocated',
        body: 'The move is saved. Their devices and alerts now follow the new area.',
    },
};
